<template>

<div class="guess">

	<div class="guess-row">

		<div class="guess-field">

			<app-input
				ref="input"
				:value="text"
				:placeholder="finished ? 'Puzzle complete' : 'Guess the game…'"
				:disabled="finished"
				aria-label="Guess the game"
				v-on:input="onInput"
				v-on:keydown="onKeydown"
				v-on:focus="onFocus"
				v-on:click.native="onFocus"
				v-on:blur="onBlur"
			/>

			<!-- spinner only while the one-time game list is preloading -->
			<span v-if="loadingList" class="guess-spinner" aria-hidden="true"></span>

			<!-- the autocomplete dropdown, filtered client-side as you type -->
			<ul v-if="open && results.length" class="guess-menu" role="listbox">
				<li
					v-for="(game, index) in results"
					:key="game.id"
					class="guess-option"
					:class="{ 'is-active': index === active }"
					role="option"
					:aria-selected="index === active"
					v-on:mousedown.prevent="select(game)"
					v-on:mouseenter="active = index"
				>
					<span class="guess-option-name">{{ game.name }}</span>
					<span v-if="game.year" class="guess-option-year">{{ game.year }}</span>
				</li>
			</ul>

		</div>

		<!-- skip: burn a guess to reveal the next tile without naming a game -->
		<button
			type="button"
			class="guess-skip"
			:disabled="finished"
			v-on:click="skip"
		>Skip</button>

	</div>

</div>

</template>

<script>

/*
	The guess autocomplete. The full ~1000-game guess pool (wider than the
	top-500 answer pool) is preloaded ONCE (alphabetical) and filtered
	client-side as you type — no per-keystroke
	request. Focusing OR clicking the field shows the list (so it reopens after a
	guess, when the field keeps focus and a plain focus event won't fire again).
	There is NO free-text submit; a guess is only committed by picking a
	suggestion. The
	Skip button burns a guess (revealing a tile) without naming a game.

	Picking emits `picked` { id, name } so the host can label guess rows; a skip
	emits { id: 0, name: 'Skipped' }.
*/

import api from '../../service/api'

// module-level cache: the game list loads once and is shared by every instance
var GAMES = null
var GAMES_PROMISE = null

// short queries (1-2 chars) match the START of the name, ignoring a leading
// article (The / A / An); 3+ chars match anywhere. This is the article-stripped,
// lowercased name used for the start-of-name test.
function sortableName(name) {

	return (name || '').toLowerCase().replace(/^(the|a|an)\s+/, '')

}

function loadGames() {

	if (GAMES) return Promise.resolve(GAMES)

	if (!GAMES_PROMISE) {

		GAMES_PROMISE = api.request('games', {}).then(function(json) {

			GAMES = (json && json.games) || []
			return GAMES

		})

	}

	return GAMES_PROMISE

}

export default {

	data: function() {

		return {
			text: '',
			results: [],
			open: false,
			active: -1,
			loadingList: !GAMES
		}

	},

	computed: {

		finished: function() {

			return this.$store.getters['game/finished']

		},

		picks: function() {

			return this.$store.state.game.picks

		}

	},

	created: function() {

		var self = this

		loadGames().then(function() {

			self.loadingList = false
			if (self.open) self.results = self.filter(self.text)

		})

	},

	methods: {

		// filter the preloaded list, excluding already-guessed games. Empty query
		// => the whole list. 1-2 chars match the START of the name (ignoring a
		// leading article); 3+ chars match anywhere (contains).
		filter: function(query) {

			var list = GAMES || []
			var picks = this.picks
			var q = (query || '').trim().toLowerCase()
			var startOnly = q.length > 0 && q.length < 3
			var out = []

			for (var i = 0; i < list.length; i++) {

				var game = list[i]

				if (picks.indexOf(game.id) !== -1) continue

				var match
				if (q === '') {
					match = true
				} else if (startOnly) {
					match = sortableName(game.name).indexOf(q) === 0
				} else {
					match = game.name.toLowerCase().indexOf(q) !== -1
				}

				if (match) out.push(game)

				// cap a typed filter for snappiness; the empty list scrolls
				if (q !== '' && out.length >= 60) break

			}

			return out

		},

		onInput: function(value) {

			this.text = value
			this.results = this.filter(value)
			this.open = true
			this.active = this.results.length ? 0 : -1

		},

		onFocus: function() {

			this.results = this.filter(this.text)
			this.open = true

		},

		onKeydown: function(event) {

			if (this.finished) return

			if (event.key === 'ArrowDown') {

				event.preventDefault()
				if (this.results.length) this.active = (this.active + 1) % this.results.length

			} else if (event.key === 'ArrowUp') {

				event.preventDefault()
				if (this.results.length) this.active = (this.active - 1 + this.results.length) % this.results.length

			} else if (event.key === 'Enter') {

				event.preventDefault()
				if (this.active >= 0 && this.results[this.active]) this.select(this.results[this.active])

			} else if (event.key === 'Escape') {

				this.open = false

			}

		},

		select: function(game) {

			if (this.finished) return

			this.$emit('picked', { id: game.id, name: game.name })
			// pass the name so the store can persist the id -> name label (survives
			// a reload); the guess action treats a {id,...} payload the same as a
			// bare id for everything else
			this.$store.dispatch('game/guess', { id: game.id, name: game.name })

			this.reset()

		},

		skip: function() {

			if (this.finished) return

			this.$emit('picked', { id: 0, name: 'Skipped' })
			this.$store.dispatch('game/guess', { id: 0, skip: true })

			this.reset()

		},

		reset: function() {

			this.text = ''
			this.results = []
			this.open = false
			this.active = -1

		},

		// delay the close so a mousedown-select still registers
		onBlur: function() {

			var self = this

			setTimeout(function() {

				self.open = false

			}, 120)

		}

	}

}

</script>

<style scoped>

.guess {
	position: relative;
}

.guess-row {
	display: flex;
	align-items: stretch;
	gap: 8px;
}

.guess-field {
	position: relative;
	flex: 1 1 auto;
	min-width: 0;
}

.guess-spinner {
	position: absolute;
	top: 50%;
	right: 12px;
	width: 15px;
	height: 15px;
	margin-top: -8px;
	border: 2px solid var(--hairline);
	border-top-color: var(--accent);
	border-radius: 50%;
	pointer-events: none;
	animation: guess-spin 700ms linear infinite;
}

.guess-skip {
	flex: none;
	padding: 0 18px;
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--ink);
	background: transparent;
	border: 1px solid var(--tile);
	border-radius: var(--radius);
	cursor: pointer;
	transition: background 140ms ease, border-color 140ms ease;
}

.guess-skip:hover:not(:disabled) {
	background: #f4eee2;
	border-color: var(--muted);
}

.guess-skip:disabled {
	opacity: 0.4;
	cursor: default;
}

.guess-menu {
	position: absolute;
	z-index: 20;
	top: calc(100% + 6px);
	left: 0;
	right: 0;
	margin: 0;
	padding: 6px;
	list-style: none;
	background: #fff;
	border: 1px solid var(--tile);
	border-radius: var(--radius);
	box-shadow: var(--shadow);
	max-height: 280px;
	overflow-y: auto;
}

.guess-option {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 11px;
	border-radius: var(--radius-sm);
	cursor: pointer;
}

.guess-option.is-active {
	background: #f4eee2;
}

.guess-option-name {
	font-family: var(--font-body);
	font-size: 14.5px;
	color: var(--ink);
}

.guess-option-year {
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.04em;
	color: var(--muted);
	flex: none;
}

@keyframes guess-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (prefers-reduced-motion: reduce) {

	.guess-spinner {
		animation-duration: 1400ms;
	}

}

</style>
